import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { grey } from '@mui/material/colors';
import { ROUTING_CONFIG } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { useIsMobile, useStore } from '@uplift/hooks';
import { UpliftBetterHelpStackedIcon } from '@uplift/icons';
import { PrivateRoute, AddressVerificationProvider, ContextualNavigationHeaderProps, ContextualNavigationLayout } from '@uplift/ui-v2';
import { useEffect, useMemo } from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { environment } from '../../../environments/environment';
import { AcctStepper, TermsAndPrivacyFooter } from './components';
import { AboutYou, KeepGoing, WelcomePage } from './pages';
import { OnboardingError } from './pages/onboarding-error';

export const OnboardingPage: React.VFC = () => {
  const { classes } = useStyles();
  const { push } = useHistory();
  const isSmall = useIsMobile('sm');
  const me = useCurrentuser();
  const { authStore } = useStore();
  const { path } = useRouteMatch();

  useEffect(() => {
    if (authStore.isLoggedIn && me?.status === 'regular') {
      push(ROUTING_CONFIG.main);
    }
  }, [ authStore.isLoggedIn, me?.status, push ]);

  const errorPage = path === ROUTING_CONFIG.onboardingError;

  const headerProps: ContextualNavigationHeaderProps = useMemo(() => {
    if (errorPage) {
      return null;
    }

    const block = isSmall ? 'rightButton' : 'centerBlock';

    const props: ContextualNavigationHeaderProps = {
      leftButton: (
        <Box flex={1} className={classes.logo}>
          <UpliftBetterHelpStackedIcon />
        </Box>
      ),
      [block]: (
        <Box justifyContent="center" textAlign="center">
          <Typography variant="h6" className={classes.title}>
            Set Up Your Account
          </Typography>
        </Box>
      ),
      hideLogo: true
    };

    return props;
  }, [ classes.logo, classes.title, errorPage, isSmall ]);

  return (
    <AddressVerificationProvider shouldReverifyAddress={false} parsedVerifiedAddress={null}>
      <ContextualNavigationLayout
        headerProps={headerProps}
        fullWidth
        progress={!errorPage && <AcctStepper step={window.location.pathname} />}
      >
        <Box className={classes.root}>
          <Box className={classes.content}>
            <Switch>
              <Route exact path={ROUTING_CONFIG.onboardingError}>
                <OnboardingError />
              </Route>
              <Route exact path={ROUTING_CONFIG.onboardingInvite}>
                {!authStore.isLoggedIn && <WelcomePage authUrl={environment.authUrl} />}
                {authStore.isLoggedIn && <KeepGoing />}
              </Route>
              <PrivateRoute
                exact
                path={ROUTING_CONFIG.onboardingGeneral}
                redirectUrl={environment.authUrl}
              >
                <AboutYou />
              </PrivateRoute>
            </Switch>
          </Box>
          <TermsAndPrivacyFooter />
        </Box>
      </ContextualNavigationLayout>
    </AddressVerificationProvider>
  );
};

const useStyles = makeStyles({ name: 'OnboardingPage' })(theme => ({
  content: {
    display: 'flex',
    flex: 1,
    maxWidth: '596px',
    padding: theme.spacing(3, 0),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(5, 0),
    }
  },
  logo: {
    display: 'flex',
    justifyContent: 'flex-start',
    color: theme.palette.darkSlate.main
  },
  root: {
    alignItems: 'center',
    backgroundColor: grey[50],
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    paddingBottom: theme.spacing(17.5),
    [theme.breakpoints.up('sm')]: {
      paddingBottom: 0
    }
  },
  title: {
    color: theme.palette.darkSlate.main,
  }
}));
